import { useFlags } from "launchdarkly-react-client-sdk";
import { Suspense, useMemo } from "react";
import {
  Navigate,
  Routes as ReactRouterRoutes,
  Route,
  useLocation
} from "react-router-dom";

import { useAppSelector } from "app/hooks";
import { NeonNowProvider } from "components/neon-now/use-neon-now.hook";
import {
  CHUNK_LOAD_RECOVERY,
  CLEARTONE_RESTRICTIONS,
  COLLECTIONS_FEATURE_ENABLED,
  DASHBOARD_WIDGETS_FEATURE,
  DISABLE_REPORTS_AND_DASHBOARD,
  ERA_CLAIMS_TAB_FEATURE,
  FAXES_PORTAL_FEATURE,
  GOOGLE_REVIEW_CONTROL_CENTER,
  HOME_SCREEN_FEATURE,
  MARKETPLACE_FEATURE,
  NEW_ERROR_SCREEN,
  VOB_REQUEST_FEATURE
} from "constants/feature-flags";
import { VIEW_OWN_REPORTS_ONLY, VIEW_REPORTS } from "constants/permissions";
import {
  ADMIN_LEVEL_GROUPS,
  GROUP_ADMINISTRATORS,
  GROUP_AUDIOLOGISTS,
  GROUP_BUSINESS_OWNERS
} from "constants/staff";
import Activate from "features/activate";
import { Layout, UnauthorizedLayout } from "features/app-layout";
import {
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentUser,
  selectLDIdentified
} from "features/auth/auth-slice";
import { CallLogDrawerProvider } from "features/calls/call-log-drawer/use-call-log-drawer";
import { AppWithGlobalChat } from "features/chat/use-global-chat.hook";
import ForgotPassword from "features/forgot-password";
import InviteAccept from "features/invite";
import { LeadsProvider } from "features/leads-ui/use-leads.hook";
import Login from "features/login";
import { MedicalScribeSectionProvider } from "features/medical-scribe/medical-scribes-section/use-medical-scribe-section.hook";
import PasswordReset from "features/password-reset";
import { PatientDrawerProvider } from "features/patients/patient-drawer";
import { PatientHeaderProvider } from "features/patients/patient-header/use-patient-header.hook";
import { PatientNotesDrawerProvider } from "features/patients/patient-notes-drawer";
import { PatientStaffTasksDrawerProvider } from "features/patients/patient-staff-tasks-drawer";
import { StaffFilterProvider } from "features/scheduling/scheduler-side-sheet/filters/staff-filter/use-staff-filter.hook";
import { ClinicsFilterProvider } from "features/scheduling/scheduler-side-sheet/filters/use-clinics-filter.hook";
import SsoCallback from "features/sso-callback";
import { StaffTasksV2Provider } from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import { ClinicNotificationsProvider } from "hooks/use-clinic-notifications";
import { EmailProvider } from "hooks/use-email.hook";
import { FaxProvider } from "hooks/use-fax.hook";
import { NewTouchpointProvider } from "hooks/use-new-touchpoint";
import useUserRoles from "hooks/use-user-roles.hook";
import { frontendLog } from "utils/debug.utils";
import {
  lazyWithRetry,
  setChunkLoadRecoveryEnabled
} from "utils/lazy-with-retry.util";

import ConfirmDialog from "../components/confirm-dialog";
import { FaxesListProvider } from "../features/faxes/faxes-filters/use-faxes-list.hook";
import { useGetMySendBirdDetailsQuery } from "../services/users";
import { RouteErrorBoundary as NewRouteErrorBoundary } from "./new-route-error-boundary.component";
import PermissionPage from "./permission-page.component";
import ProtectedRoute from "./protected-route.component";
import RequireAuth from "./required-auth";
import { RouteErrorBoundary as PreviousRouteErrorBoundary } from "./route-error-boundary.component";
import { RouteSuspenseFallback } from "./route-suspense-fallback.component";

// Lazy loaded routes
const SchedulingRoutesLazy = lazyWithRetry(
  () => import("features/scheduling/scheduling.routes"),
  "scheduling.routes"
);

const LeadsUIRoutesLazy = lazyWithRetry(
  () => import("features/leads-ui/leads-ui.routes"),
  "leads-ui.routes"
);

const BillingRoutesLazy = lazyWithRetry(
  () => import("features/billing/billing.routes"),
  "billing.routes"
);

const CollectionsRoutesLazy = lazyWithRetry(
  () => import("features/collections/collections.routes"),
  "collections.routes"
);

const CallsRoutesLazy = lazyWithRetry(
  () => import("features/calls/calls.routes"),
  "calls.routes"
);

const ChatLazy = lazyWithRetry(
  () => import("features/chat/chat.component"),
  "chat.component"
);

const DashboardRoutesLazy = lazyWithRetry(
  () => import("features/dashboard/dashboard.routes"),
  "dashboard.routes"
);

const InboxRoutesLazy = lazyWithRetry(
  () => import("features/inbox/inbox.routes"),
  "inbox.routes"
);

const InventoryRoutesLazy = lazyWithRetry(
  () => import("features/inventory/inventory.routes"),
  "inventory.routes"
);

const MarketingRoutesLazy = lazyWithRetry(
  () => import("features/marketing/marketing.routes"),
  "marketing.routes"
);

const MarketplaceRoutesLazy = lazyWithRetry(
  () => import("features/marketplace/marketplace.routes"),
  "marketplace.routes"
);

const MedicalScribesListLazy = lazyWithRetry(
  () =>
    import(
      "features/medical-scribe/medical-scribes-section/medical-scribes-section.component"
    ),
  "medical-scribes-section.component"
);

const PatientsRoutesLazy = lazyWithRetry(
  () => import("features/patients/patients.routes"),
  "patients.routes"
);

const QuickbooksRoutesLazy = lazyWithRetry(
  () => import("features/quickbooks/quickbooks.routes"),
  "quickbooks.routes"
);

const ReviewsRoutesLazy = lazyWithRetry(
  () => import("features/reviews/reviews.routes"),
  "reviews.routes"
);

const PodiumOauthCallbackLazy = lazyWithRetry(
  () => import("features/podium/podium-oauth-callback.component"),
  "podium-oauth-callback.component"
);

const ZoomOauthCallbackLazy = lazyWithRetry(
  () => import("features/settings/zoom/zoom-oauth-callback.component"),
  "zoom-oauth-callback.component"
);

const GoogleReviewsOauthCallbackLazy = lazyWithRetry(
  () =>
    import(
      "features/settings/google-reviews/google-reviews-oauth-callback.component"
    ),
  "google-reviews-oauth-callback.component"
);

const ReportsRoutesLazy = lazyWithRetry(
  () => import("features/reports/reports.routes"),
  "reports.routes"
);

const SettingsRoutesLazy = lazyWithRetry(
  () => import("features/settings/settings.routes"),
  "settings.routes"
);

const PracticeSettingsRoutesLazy = lazyWithRetry(
  () => import("features/practice-settings/practice-settings.routes"),
  "practice-settings.routes"
);

const AdministrationRoutesLazy = lazyWithRetry(
  () => import("../features/administration/administration.routes"),
  "administration.routes"
);

const FaxesLazy = lazyWithRetry(() => import("../features/faxes"), "faxes");

const InsuranceRoutesLazy = lazyWithRetry(
  () => import("../features/insurance/insurance.routes"),
  "insurance.routes"
);

const VobListingRoutesLazy = lazyWithRetry(
  () => import("features/vob-listing/vob-listing.routes"),
  "vob-listing.routes"
);

const HomeLazy = lazyWithRetry(
  () => import("features/home/home.component"),
  "home.component"
);

export default function Routes() {
  const flags = useFlags();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const { isScheduler, canAccessDashboards, canAccessQBO } = useUserRoles();
  const launchDarklyIdentified = useAppSelector(selectLDIdentified);
  const location = useLocation();

  const isNewErrorScreenEnabled = flags[NEW_ERROR_SCREEN] ?? false;

  // The lazy import factories run outside React, so the flag is synced into
  // the lazy-with-retry module instead of being read via useFlags there.
  // Synced during render (idempotent), not in an effect: a route chunk can
  // fail while the first post-flags render is still committing, before
  // effects flush. Known limitation either way: a chunk failing before
  // LaunchDarkly resolves sees the flag as false and goes straight to the
  // error boundary (no auto-reload).
  setChunkLoadRecoveryEnabled(flags[CHUNK_LOAD_RECOVERY] ?? false);

  const RouteErrorBoundary = useMemo(() => {
    return isNewErrorScreenEnabled
      ? NewRouteErrorBoundary
      : PreviousRouteErrorBoundary;
  }, [isNewErrorScreenEnabled]);

  const { data: sendbirdDetails } = useGetMySendBirdDetailsQuery(undefined, {
    skip: !currentUser?.sendbird_user_id
  });

  const isAdministrationPageEnabled =
    currentUser?.has_access_to_admin_panel ?? true;
  const isERAClaimsTabEnabled = flags[ERA_CLAIMS_TAB_FEATURE] ?? true;
  const isCleartoneRestrictionsEnabled = flags[CLEARTONE_RESTRICTIONS];
  const isFaxesPortalFeatureEnabled = flags[FAXES_PORTAL_FEATURE] ?? true;
  const isMarketplaceFeatureEnabled = flags[MARKETPLACE_FEATURE] ?? false;
  const isCollectionsFeatureEnabled =
    flags[COLLECTIONS_FEATURE_ENABLED] ?? true;
  const isClaimMdConnected =
    currentClinic?.is_claim_md_account_key_set ||
    currentPractice?.is_claim_md_account_key_set;
  const isVobRequestFeatureEnabled =
    (flags[VOB_REQUEST_FEATURE] ?? false) && isClaimMdConnected;
  const isGoogleReviewControlCenterEnabled =
    flags[GOOGLE_REVIEW_CONTROL_CENTER] ?? false;
  const isReportsAndDashboardDisabled =
    flags[DISABLE_REPORTS_AND_DASHBOARD] ?? false;
  const isHomeScreenEnabled = flags[HOME_SCREEN_FEATURE] ?? false;
  const isDashboardWidgetsEnabled =
    (flags[DASHBOARD_WIDGETS_FEATURE] ?? false) &&
    Boolean(currentPractice?.enable_dashboard_widgets);
  const showHomeRoute = isHomeScreenEnabled || isDashboardWidgetsEnabled;

  const inAppChatFullyEnabled =
    (currentPractice?.sendbird_chat_enabled &&
      currentUser?.sendbird_user_id &&
      sendbirdDetails?.access_token) ??
    false;

  frontendLog(["feature-flags", JSON.parse(JSON.stringify(flags))]);
  return (
    <ReactRouterRoutes>
      <Route element={<UnauthorizedLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/password-reset/:uid/:token" element={<PasswordReset />} />
        <Route path="/activate/:uid/:token" element={<Activate />} />
        {/* Public invite acceptance — invitee isn't logged in yet. */}
        <Route path="/accept/:token" element={<InviteAccept />} />
      </Route>
      {/* SSO landing — the backend redirects here with the result in the URL
          hash (#auth_token= on success, #sso_error= on failure). One component
          serves both. Runs BEFORE auth exists, so it must NOT be RequireAuth-
          wrapped and is OUTSIDE UnauthorizedLayout so the layout's post-login
          auto-redirect can't fire before the fail-closed practice check. */}
      <Route path="/sso/complete" element={<SsoCallback />} />
      <Route path="/sso/error" element={<SsoCallback />} />
      {/* Always register OAuth callback routes.
          Feature flags can be undefined during initial load, and if this route
          isn't registered, the "/" route + its "*" child can catch "/oauth/..."
          and redirect away before the callback runs. */}
      <Route
        path="/oauth/podium/callback"
        element={
          <RequireAuth>
            <RouteErrorBoundary routeName="Podium OAuth">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <PodiumOauthCallbackLazy />
              </Suspense>
            </RouteErrorBoundary>
          </RequireAuth>
        }
      />
      <Route
        path="/oauth/zoom/callback"
        element={
          <RequireAuth>
            <RouteErrorBoundary routeName="Zoom OAuth">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <ZoomOauthCallbackLazy />
              </Suspense>
            </RouteErrorBoundary>
          </RequireAuth>
        }
      />
      <Route
        path="/oauth/google-reviews/callback"
        element={
          <RequireAuth>
            <RouteErrorBoundary routeName="Google Reviews OAuth">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <GoogleReviewsOauthCallbackLazy />
              </Suspense>
            </RouteErrorBoundary>
          </RequireAuth>
        }
      />
      {isAdministrationPageEnabled && (
        <Route
          path="administration/*"
          element={
            <RequireAuth>
              <>
                <RouteErrorBoundary routeName="Administration">
                  <Suspense fallback={<RouteSuspenseFallback />}>
                    <ClinicNotificationsProvider>
                      <NeonNowProvider>
                        <AdministrationRoutesLazy />
                      </NeonNowProvider>
                    </ClinicNotificationsProvider>
                  </Suspense>
                </RouteErrorBoundary>
                <ConfirmDialog />
              </>
            </RequireAuth>
          }
        />
      )}

      <Route
        path="leads/*"
        element={
          <RouteErrorBoundary routeName="Leads">
            <Suspense fallback={<RouteSuspenseFallback />}>
              <RequireAuth>
                <ProtectedRoute>
                  <LeadsProvider>
                    <LeadsUIRoutesLazy />
                    <ConfirmDialog />
                  </LeadsProvider>
                </ProtectedRoute>
              </RequireAuth>
            </Suspense>
          </RouteErrorBoundary>
        }
      />

      <Route
        path="/"
        element={
          <RequireAuth>
            <PatientHeaderProvider>
              <PatientDrawerProvider>
                <ClinicNotificationsProvider>
                  <StaffTasksV2Provider>
                    <PatientNotesDrawerProvider>
                      <PatientStaffTasksDrawerProvider>
                        <FaxProvider>
                          <EmailProvider>
                            <NewTouchpointProvider>
                              <CallLogDrawerProvider>
                                <NeonNowProvider>
                                  {currentPractice?.sendbird_chat_enabled &&
                                  currentUser?.sendbird_user_id ? (
                                    <AppWithGlobalChat>
                                      <Layout />
                                    </AppWithGlobalChat>
                                  ) : (
                                    <Layout />
                                  )}
                                </NeonNowProvider>
                                <ConfirmDialog />
                              </CallLogDrawerProvider>
                            </NewTouchpointProvider>
                          </EmailProvider>
                        </FaxProvider>
                      </PatientStaffTasksDrawerProvider>
                    </PatientNotesDrawerProvider>
                  </StaffTasksV2Provider>
                </ClinicNotificationsProvider>
              </PatientDrawerProvider>
            </PatientHeaderProvider>
          </RequireAuth>
        }
      >
        <Route path="permission" element={<PermissionPage />} />
        <Route
          path=""
          element={
            currentPractice ? (
              <Navigate
                to={{
                  pathname: showHomeRoute ? "home" : "scheduling",
                  search: location.search
                }}
                replace
              />
            ) : (
              <RouteSuspenseFallback />
            )
          }
        />
        {showHomeRoute && (
          /* Single /home route owned by HomeLazy. Dashboard widgets render as
             an "At a Glance" tab inside HomeContent when both the LaunchDarkly
             DASHBOARD_WIDGETS_FEATURE flag and Practice.enable_dashboard_widgets
             are true. Per-role widget visibility is enforced by the backend. */
          <Route
            path="home"
            element={
              <RouteErrorBoundary routeName="Home">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  <HomeLazy />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        )}
        {!showHomeRoute && (
          /* Fallback: if every gate is off and the user lands at /home (stale
             link, bookmark, or mid-session flag flip) redirect to scheduling
             instead of rendering a blank panel. */
          <Route
            path="home/*"
            element={<Navigate to="/scheduling" replace />}
          />
        )}
        {!isReportsAndDashboardDisabled && canAccessDashboards && (
          <Route
            path="dashboard/*"
            element={
              <RouteErrorBoundary routeName="Dashboard">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  <DashboardRoutesLazy />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        )}
        {!isScheduler && (
          <Route
            path="inbox/*"
            element={
              <RouteErrorBoundary routeName="Inbox">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  <InboxRoutesLazy />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        )}
        {!isScheduler && (
          <Route
            path="inventory/*"
            element={
              <RouteErrorBoundary routeName="Inventory">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  {isCleartoneRestrictionsEnabled ? (
                    <ProtectedRoute roles={ADMIN_LEVEL_GROUPS}>
                      <InventoryRoutesLazy />
                    </ProtectedRoute>
                  ) : (
                    <InventoryRoutesLazy />
                  )}
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        )}
        {!isScheduler && !isReportsAndDashboardDisabled && (
          <Route
            path="reports/*"
            element={
              <ProtectedRoute
                roles={ADMIN_LEVEL_GROUPS}
                permissions={[VIEW_OWN_REPORTS_ONLY, VIEW_REPORTS]}
              >
                <RouteErrorBoundary routeName="Reports">
                  <Suspense fallback={<RouteSuspenseFallback />}>
                    <ReportsRoutesLazy />
                  </Suspense>
                </RouteErrorBoundary>
              </ProtectedRoute>
            }
          />
        )}
        <Route
          path="scheduling/*"
          element={
            <RouteErrorBoundary routeName="Scheduling">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <ClinicsFilterProvider>
                  <StaffFilterProvider>
                    <SchedulingRoutesLazy />
                  </StaffFilterProvider>
                </ClinicsFilterProvider>
              </Suspense>
            </RouteErrorBoundary>
          }
        />
        {!isScheduler && (
          <Route
            path="billing/*"
            element={
              <RouteErrorBoundary routeName="Billing">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  {isCleartoneRestrictionsEnabled ? (
                    <ProtectedRoute roles={ADMIN_LEVEL_GROUPS}>
                      <BillingRoutesLazy />
                    </ProtectedRoute>
                  ) : (
                    <BillingRoutesLazy />
                  )}
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        )}
        {!isScheduler &&
          (location.pathname.startsWith("/collections") ||
            ((!launchDarklyIdentified || isCollectionsFeatureEnabled) &&
              currentPractice?.enrolled_in_collections &&
              currentUser?.has_access_to_collections)) && (
            <Route
              path="collections/*"
              element={
                <RouteErrorBoundary routeName="Collections">
                  <Suspense fallback={<RouteSuspenseFallback />}>
                    <CollectionsRoutesLazy />
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
          )}
        {!isScheduler && isERAClaimsTabEnabled && (
          <Route
            path="eras/*"
            element={
              <RouteErrorBoundary routeName="Insurance">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  {isCleartoneRestrictionsEnabled ? (
                    <ProtectedRoute roles={ADMIN_LEVEL_GROUPS}>
                      <InsuranceRoutesLazy />
                    </ProtectedRoute>
                  ) : (
                    <InsuranceRoutesLazy />
                  )}
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        )}
        {(!launchDarklyIdentified ||
          isVobRequestFeatureEnabled ||
          location.pathname.startsWith("/vobs")) && (
          <Route
            path="vobs/*"
            element={
              <RouteErrorBoundary routeName="VOBs">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  <VobListingRoutesLazy />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        )}
        <Route
          path="patients/*"
          element={
            <RouteErrorBoundary routeName="Patients">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <PatientsRoutesLazy />
              </Suspense>
            </RouteErrorBoundary>
          }
        />
        <Route
          path="settings/*"
          element={
            <RouteErrorBoundary routeName="Settings">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <SettingsRoutesLazy />
              </Suspense>
            </RouteErrorBoundary>
          }
        />
        <Route
          path="practice-settings/*"
          element={
            <RouteErrorBoundary routeName="PracticeSettings">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <PracticeSettingsRoutesLazy />
              </Suspense>
            </RouteErrorBoundary>
          }
        />
        {!isScheduler && (
          <Route
            path="marketing/*"
            element={
              <RouteErrorBoundary routeName="Marketing">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  <MarketingRoutesLazy />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        )}
        {!isScheduler && (
          <Route
            path="calls/*"
            element={
              <ProtectedRoute roles={ADMIN_LEVEL_GROUPS}>
                <RouteErrorBoundary routeName="Calls">
                  <Suspense fallback={<RouteSuspenseFallback />}>
                    <CallsRoutesLazy />
                  </Suspense>
                </RouteErrorBoundary>
              </ProtectedRoute>
            }
          />
        )}
        {!isScheduler && canAccessQBO && (
          <Route
            path="quickbooks/*"
            element={
              <RouteErrorBoundary routeName="QuickBooks">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  <QuickbooksRoutesLazy />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        )}
        {!isScheduler &&
          (!launchDarklyIdentified || isGoogleReviewControlCenterEnabled) && (
            <Route
              path="reviews/*"
              element={
                <ProtectedRoute
                  roles={[
                    GROUP_BUSINESS_OWNERS,
                    GROUP_ADMINISTRATORS,
                    GROUP_AUDIOLOGISTS
                  ]}
                >
                  <RouteErrorBoundary routeName="Reviews">
                    <Suspense fallback={<RouteSuspenseFallback />}>
                      <ReviewsRoutesLazy />
                    </Suspense>
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
          )}
        {!isScheduler &&
          (!launchDarklyIdentified ||
            isFaxesPortalFeatureEnabled ||
            location.pathname.startsWith("/faxes-portal")) && (
            <Route
              path="faxes-portal/*"
              element={
                <FaxesListProvider>
                  <RouteErrorBoundary routeName="Faxes">
                    <Suspense fallback={<RouteSuspenseFallback />}>
                      <FaxesLazy />
                    </Suspense>
                  </RouteErrorBoundary>
                </FaxesListProvider>
              }
            />
          )}
        {!isScheduler &&
          (!launchDarklyIdentified ||
            isMarketplaceFeatureEnabled ||
            location.pathname.startsWith("/marketplace")) && (
            <Route
              path="marketplace/*"
              element={
                <ProtectedRoute roles={[GROUP_BUSINESS_OWNERS]}>
                  <RouteErrorBoundary routeName="Marketplace">
                    <Suspense fallback={<RouteSuspenseFallback />}>
                      <MarketplaceRoutesLazy />
                    </Suspense>
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
          )}
        <Route
          path="medical-scribes/*"
          element={
            <MedicalScribeSectionProvider>
              <RouteErrorBoundary routeName="Medical Scribes">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  <MedicalScribesListLazy />
                </Suspense>
              </RouteErrorBoundary>
            </MedicalScribeSectionProvider>
          }
        />
        {!isScheduler && inAppChatFullyEnabled && (
          <Route
            path="chat/*"
            element={
              <RouteErrorBoundary routeName="Chat">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  <ChatLazy />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        )}

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </ReactRouterRoutes>
  );
}
